// 현장 목록·상세의 날짜 표시 — 「오늘」은 서버 시간대가 아니라 KST 로 센다.

import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import { APP_TZ, EMPTY_MARK } from "../../lib/format";
import type { Project, ProjectPhase } from "./types";

dayjs.extend(utc);
dayjs.extend(timezone);

const DAY_MS = 86_400_000;

export const todayKst = (now: Date = new Date()): string =>
  dayjs(now).tz(APP_TZ).format("YYYY-MM-DD");

// 현장 이름 — 제목이 비면 현장 주소, 둘 다 비면 「—」(목록·차트·달력 한 벌).
export const projectNameOf = (project: Pick<Project, "title" | "site_address">): string =>
  project.title || project.site_address || EMPTY_MARK;

// 착공 예정일까지 남은 날 — 양수면 D-n, 0 이면 D-day, 음수면 지난 날 수. 날짜가 없으면 null.
export const dDayOf = (planned: string | null, today: string): number | null => {
  if (planned === null) return null;
  return Math.round(
    (Date.parse(`${planned}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY_MS
  );
};

export const dDayLabel = (days: number | null): string => {
  if (days === null) return "";
  if (days === 0) return "D-day";
  return days > 0 ? `D-${days}` : `D+${-days}`;
};

type StartDayProject = Pick<Project, "status" | "planned_start_date">;

// 착공 전이고 착공 예정일이 오늘 — 목록·대시보드의 「오늘 착공」.
export const isStartToday = (project: StartDayProject, today: string): boolean =>
  project.status === "SCHEDULED" && project.planned_start_date === today;

// 착공 전인데 착공 예정일이 이미 지났다(오늘은 아니다) — 목록·대시보드의 「착공일 지남」.
export const isStartOverdue = (project: StartDayProject, today: string): boolean =>
  project.status === "SCHEDULED" &&
  project.planned_start_date !== null &&
  project.planned_start_date < today;

export type ScheduleNotice = "holidays-missing" | "unscheduled" | null;

// 일정 칸 안내 — 날짜 있는 공정이 공휴일 없이 펼쳐졌으면 미반영, 공정은 있는데 날짜가 하나도 없으면 착공일 대기.
export const scheduleNoticeOf = (
  project: Pick<Project, "schedule_holidays">,
  phases: readonly Pick<ProjectPhase, "start_date">[]
): ScheduleNotice => {
  if (phases.length === 0) return null;
  if (phases.every((p) => p.start_date === null)) return "unscheduled";
  return project.schedule_holidays === null ? "holidays-missing" : null;
};
